import type { Project, WebsiteDiscovery } from '@shared/api-client';

export const normalizedWebsite = (value?: string | null): string | null => {
  if (!value?.trim()) {
    return null;
  }
  try {
    const url = new URL(
      /^https?:\/\//i.test(value.trim())
        ? value.trim()
        : `https://${value.trim()}`,
    );
    if (
      !['http:', 'https:'].includes(url.protocol) ||
      url.username ||
      url.password ||
      url.port ||
      !url.hostname.includes('.')
    ) {
      return null;
    }
    url.hash = '';
    url.search = '';
    return url.href;
  } catch {
    return null;
  }
};

/** Keep existing explicit restrictions; only add app hosts selected during review. */
export const setupHostnames = (
  website: string | null,
  selected: string[],
  project?: Project,
  additional: string[] = [],
): string[] | null => {
  if (!website) {
    return null;
  }
  const domain = new URL(website).hostname;
  const root = domain.replace(/^www\./, '');
  const sameDomain = project?.primaryDomain?.replace(/^www\./, '') === root;
  const existing = sameDomain ? project?.allowedHostnames : undefined;
  if (sameDomain && !existing?.length) {
    return existing ?? null;
  }
  return [
    ...new Set([
      ...(existing ?? [root, `www.${root}`]),
      ...additional,
      ...selected.filter((host) => host === root || host.endsWith(`.${root}`)),
    ]),
  ];
};

export const discoveryMatchesWebsite = (
  discovery: WebsiteDiscovery | null,
  website: string,
) =>
  Boolean(
    discovery &&
    normalizedWebsite(discovery.websiteUrl) === normalizedWebsite(website),
  );

export const parseAdditionalHostnames = (value: string): string[] => {
  const hosts = value
    .split(/[\s,]+/)
    .filter(Boolean)
    .map((host) => {
      const message =
        'Enter exact app domains, such as app.example.com, separated by commas.';
      let url: URL;
      try {
        url = new URL(`https://${host}`);
      } catch {
        throw new Error(message);
      }
      if (
        url.hostname !== host.toLowerCase() ||
        !host.includes('.') ||
        url.port ||
        url.username ||
        url.password ||
        url.pathname !== '/' ||
        url.search ||
        url.hash ||
        host.includes('*')
      ) {
        throw new Error(message);
      }
      return url.hostname;
    });
  if (hosts.length > 8) {
    throw new Error('Add up to eight additional app domains.');
  }
  return [...new Set(hosts)];
};
